import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  VideoCameraIcon,
  XMarkIcon,
  ClockIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button, Modal } from '@theraply/ui';
import {
  useConsumerAppointments,
  useCancelAppointment,
  type ConsumerAppointment,
} from '@theraply/api-client';

type TabKey = 'upcoming' | 'past' | 'cancelled';

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function getSessionTypeLabel(type: string): string {
  switch (type) {
    case 'INTAKE': return 'Initial Consultation';
    case 'REGULAR': return 'Regular Session';
    case 'CRISIS': return 'Crisis Session';
    default: return type;
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'CONFIRMED':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Confirmed</span>;
    case 'PENDING_PAYMENT':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending Payment</span>;
    case 'CANCELLED':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Cancelled</span>;
    case 'COMPLETED':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Completed</span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
  }
}

function isUpcoming(appointment: ConsumerAppointment): boolean {
  const now = new Date();
  const appointmentDate = new Date(`${appointment.slotDate}T${appointment.slotTime}`);
  return appointmentDate >= now && (appointment.status === 'CONFIRMED' || appointment.status === 'PENDING_PAYMENT');
}

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('upcoming');
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [detailsAppointment, setDetailsAppointment] = useState<ConsumerAppointment | null>(null);

  const { data, isLoading, error, refetch } = useConsumerAppointments();
  const cancelMutation = useCancelAppointment();

  const allAppointments = data?.bookings || [];

  const filteredAppointments = allAppointments.filter((a) => {
    switch (activeTab) {
      case 'upcoming':
        return isUpcoming(a);
      case 'past':
        return a.status === 'COMPLETED';
      case 'cancelled':
        return a.status === 'CANCELLED';
      default:
        return true;
    }
  }).sort((a, b) => {
    const dateA = `${a.slotDate}T${a.slotTime}`;
    const dateB = `${b.slotDate}T${b.slotTime}`;
    return activeTab === 'upcoming'
      ? dateA.localeCompare(dateB)
      : dateB.localeCompare(dateA);
  });

  const handleCancel = async () => {
    if (!cancellingId) return;
    try {
      await cancelMutation.mutateAsync({
        bookingId: cancellingId,
        reason: cancelReason || undefined,
      });
      setCancellingId(null);
      setCancelReason('');
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
    }
  };

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'upcoming', label: 'Upcoming', count: allAppointments.filter(isUpcoming).length },
    { key: 'past', label: 'Past', count: allAppointments.filter((a) => a.status === 'COMPLETED').length },
    { key: 'cancelled', label: 'Cancelled', count: allAppointments.filter((a) => a.status === 'CANCELLED').length },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Appointments</h3>
          <p className="text-red-600 text-sm">
            {(error as any)?.message || 'Failed to load your appointments. Please try again.'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Appointments</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your therapy appointments.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate('/therapists')}
        >
          Book New Session
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span
                className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-indigo-100 text-indigo-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Appointment Cards */}
      {filteredAppointments.length > 0 ? (
        <div className="space-y-4">
          {filteredAppointments.map((appointment) => (
            <div
              key={appointment.id}
              className="bg-white shadow rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <UserCircleIcon className="h-8 w-8 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {appointment.therapistFirstName} {appointment.therapistLastName}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarDaysIcon className="h-4 w-4" />
                        {formatDate(appointment.slotDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {formatTime(appointment.slotTime)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                        {getSessionTypeLabel(appointment.sessionType)}
                      </span>
                      {getStatusBadge(appointment.status)}
                    </div>
                    {appointment.notes && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {appointment.notes}
                      </p>
                    )}
                    {appointment.cancellationReason && (
                      <p className="mt-2 text-sm text-red-600">
                        Reason: {appointment.cancellationReason}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  {isUpcoming(appointment) && appointment.status === 'CONFIRMED' && (
                    <>
                      <button
                        onClick={() => navigate(`/session/${appointment.id}`)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                      >
                        <VideoCameraIcon className="h-4 w-4" />
                        Join
                      </button>
                      <button
                        onClick={() => setCancellingId(appointment.id)}
                        className="flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors text-sm font-medium"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setDetailsAppointment(appointment)}
                    className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <CalendarDaysIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {activeTab === 'upcoming'
              ? 'No Upcoming Appointments'
              : activeTab === 'past'
              ? 'No Past Appointments'
              : 'No Cancelled Appointments'}
          </h3>
          <p className="text-gray-600">
            {activeTab === 'upcoming'
              ? "You don't have any upcoming appointments. Book a session to get started."
              : activeTab === 'past'
              ? "You haven't completed any sessions yet."
              : "You don't have any cancelled appointments."}
          </p>
          {activeTab === 'upcoming' && (
            <button
              onClick={() => navigate('/therapists')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Find a Therapist
            </button>
          )}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={cancellingId !== null}
        onClose={() => { setCancellingId(null); setCancelReason(''); }}
        title="Cancel Appointment"
        size="md"
      >
        <div>
          <p className="text-gray-600 mb-4">
            Are you sure you want to cancel this appointment? This action cannot be undone.
          </p>
          <div className="mb-6">
            <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for cancellation (optional)
            </label>
            <textarea
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Please let us know why you're cancelling..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => { setCancellingId(null); setCancelReason(''); }}
              disabled={cancelMutation.isPending}
            >
              Keep Appointment
            </Button>
            <button
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Appointment'}
            </button>
          </div>
          {cancelMutation.isError && (
            <p className="mt-3 text-sm text-red-600">
              Failed to cancel appointment. Please try again.
            </p>
          )}
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={detailsAppointment !== null}
        onClose={() => setDetailsAppointment(null)}
        title="Appointment Details"
        size="md"
      >
        {detailsAppointment && (
          <div>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Therapist</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {detailsAppointment.therapistFirstName} {detailsAppointment.therapistLastName}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Date</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(detailsAppointment.slotDate)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Time</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatTime(detailsAppointment.slotTime)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Session Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{getSessionTypeLabel(detailsAppointment.sessionType)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">{getStatusBadge(detailsAppointment.status)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Amount</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  ${(detailsAppointment.amountCents / 100).toFixed(2)}
                </dd>
              </div>
              {detailsAppointment.notes && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">{detailsAppointment.notes}</dd>
                </div>
              )}
              {detailsAppointment.cancellationReason && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cancellation Reason</dt>
                  <dd className="mt-1 text-sm text-red-600">{detailsAppointment.cancellationReason}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Booked On</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(detailsAppointment.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </dd>
              </div>
            </dl>
            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={() => setDetailsAppointment(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
